import React, { useState, useEffect, useMemo } from 'react';
import { translate } from '../Services/translation';
import * as debounce from 'lodash.debounce';
import { withGoogleReCaptcha } from 'react-google-recaptcha-v3';

import MapAndAddress from '../Components/SunPanelsByRoof.MapAndAddress';
import VariantSelector from '../Components/SunPanelsByRoof.VariantSelector';
import AmountSlider from '../Components/SunPanelsByRoof.AmountSlider';
import SellingPoints from '../Components/SunPanelsByRoof.SellingPoints';
import EstimatedOfferCard from '../Components/SunPanelsByRoof.EstimatedOfferCard';
import YourRoof from '../Components/SunPanelsByRoof.YourRoof';
import SunPanelsContactInfoUserForm from '../Components/SunPanelsContactInfoUserForm';
import PropTypes from 'prop-types';

import sunPanelService from '../api/sunPanelService';

const MIN_PANELS = 8;
const PANELS_STEP_SIZE = 1;
const MAX_OFFERS_PER_REQUEST = 100;
const MAX_PANELS = (MAX_OFFERS_PER_REQUEST * PANELS_STEP_SIZE + MIN_PANELS) / 2;

const REQ_STATUS = {
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
  IDLE: 'idle',
};

const DEFAULT_ROOF_ID = 3; // DEFAULT ROOF TYPE IS "Dobbelkrum takstein"

const BASE_SUPPORT_DISCOUNT = 7500;
const SUPPORT_PER_KWP = 1250;
const MAX_KWP = 20;
const MAX_SUPPORT_DISCOUNT = BASE_SUPPORT_DISCOUNT + MAX_KWP * SUPPORT_PER_KWP;

const getSupportDiscount = (kwp) => {
  const kwpValue = Math.floor(kwp / 1000);
  if (kwpValue > MAX_KWP) {
    return MAX_SUPPORT_DISCOUNT;
  }
  return BASE_SUPPORT_DISCOUNT + kwpValue * SUPPORT_PER_KWP;
};

// ? VALUES FOR NORWAY
const MARGIN_PERCENTAGE = 0.15;
const VAT_PERCENTAGE = 0.25;

const calculateComboPrice = (selectedCombo) => {
  const price = selectedCombo.montasje + selectedCombo.materiell + selectedCombo.elektriker;
  const p = price / (1 - MARGIN_PERCENTAGE) * (1 + VAT_PERCENTAGE);
  return Math.round(p);
};

const SunPanelsByRoof = ({
  title,
  mapAddressTitle,
  sunPanelPageUrl,
  variantSelectorTitle,
  amountSliderTitle,
  yourRoofTitle,
  yourRoofPreamble,
  yourRoofDefaultTypePreamble,
  yourRoofText,
  offerTitle,
  errorTitle,
  errorText,
  noOfferTitle,
  noOfferText,
  thanksTitle,
  thanksText,
  yourRoofModelTitle,
  apiKey,
  googleReCaptchaProps,
}) => {
  const { executeRecaptcha } = googleReCaptchaProps;

  const STEP_RESULT = 1;
  const STEP_MAKE_CONTACT = 2;
  const urlParams = new URLSearchParams(window.location.search);
  const lat = Number(urlParams.get('lat'));
  const lng = Number(urlParams.get('lng'));

  // UI STATES
  const [step, setStep] = useState(1);
  const [amountPanels, setAmountPanels] = useState(MIN_PANELS);
  const [norkartData, setNorkartData] = useState(null);
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestError, setRequestError] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [hiddenTermsAndConditions, setHiddenTermsAndConditions] = useState(true);
  const [acceptedConditions, setAcceptedConditions] = useState(false);

  // DATA STATES
  const [roofTypeId, setRoofTypeId] = useState(null);
  const [roofTypesData, setRoofTypesData] = useState({
    roofTypes: [],
    status: REQ_STATUS.IDLE,
  });
  const [offersData, setOffersData] = useState({
    offers: [],
    status: REQ_STATUS.IDLE,
  });
  const [variantsData, setVariantsData] = useState({
    variants: [],
    status: REQ_STATUS.IDLE,
  });
  const [selectedVariantId, setSelectedVariantId] = useState(null);

  // ? MEMOIZED VALUES
  const selectedRoofType = useMemo(() => {
    if (!roofTypesData || !roofTypesData.roofTypes || !roofTypeId) return null;
    return roofTypesData.roofTypes.find((roofType) => roofType.id === roofTypeId);
  }, [roofTypesData, roofTypeId]);

  const selectedOffer = useMemo(() => {
    if (!offersData.offers?.length || !selectedVariantId) return null;
    return offersData.offers.find((offer) => offer.variant === selectedVariantId);
  }, [offersData.offers, selectedVariantId]);

  const selectedPanelCombo = useMemo(() => {
    if (!selectedOffer) return null;
    return selectedOffer.paneler.find(
      (combo) => combo.antallPaneler === amountPanels
    );
  }, [selectedOffer, amountPanels]);

  const supportDiscount = useMemo(() => {
    if (!selectedPanelCombo) return null;
    return getSupportDiscount(selectedPanelCombo.kwp);
  }, [selectedPanelCombo]);

  const parameters = useMemo(() => {
    if (!norkartData || !roofTypeId || !variantsData.variants?.length)
      return null;

    const { Helning, Retning, Lengde, Bredde, Areal2D, Areal3D } = norkartData;
    const geometri = JSON.parse(norkartData.Geometri);

    return variantsData.variants.map((variant) => ({
      lat,
      lng,
      helning: Helning,
      retning: Retning,
      lengde: Lengde,
      bredde: Bredde,
      areal2d: Areal2D,
      areal3d: Areal3D,
      eaveheight: 4,
      typetak: roofTypeId,
      produkttype: variant.id,
      kalkulerfra: MIN_PANELS,
      kalkulertil: MAX_PANELS,
      kalkulertrinn: PANELS_STEP_SIZE,
      geometri,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [norkartData, roofTypeId, variantsData.variants]);

  const loading = useMemo(
    () => {
      const itemsStatusToCheckLoading = [
        offersData.status,
        variantsData.status,
        roofTypesData.status,
      ];
      return (
        itemsStatusToCheckLoading.includes(REQ_STATUS.LOADING) ||
        itemsStatusToCheckLoading.includes(REQ_STATUS.IDLE)
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [offersData.status, variantsData.status, roofTypesData.status]
  );

  // ? debouncing functions so we don't send request too many times
  const debouncedSetRoofType = debounce(
    (roofTypeId) => setRoofTypeId(roofTypeId),
    1000
  );

  // ? 1 - FETCH NORKART DATA
  useEffect(() => {
    if (lat && lng) {
      sunPanelService.getNorkartData(lat, lng, apiKey).then((data) => {
        setNorkartData(data[0]);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ? 2 - FETCH ROOF TYPES DATA
  useEffect(() => {
    // ? Don't fetch if we don't have norkart data
    if (!norkartData) return;

    setRoofTypesData({ roofTypes: [], status: REQ_STATUS.LOADING });

    sunPanelService
      .getRoofTypes()
      .then((data) => {
        const parsedData = JSON.parse(data);
        setRoofTypesData({ roofTypes: parsedData, status: REQ_STATUS.SUCCESS });

        const defaultRoof = parsedData.find(
          (roofType) => roofType.id === DEFAULT_ROOF_ID
        );
        setRoofTypeId(defaultRoof?.id || parsedData[0].id);
      })
      .catch(() => {
        setRoofTypesData({ roofTypes: [], status: REQ_STATUS.ERROR });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [norkartData]);

  // ? 3 - FETCH VARIANTS DATA
  useEffect(() => {
    // ? Don't fetch if we don't have norkart data
    // ? Don't fetch if we already have variantS
    if (!norkartData || !!variantsData.variants?.length) return;

    setVariantsData({ variants: [], status: REQ_STATUS.LOADING });

    const headers = new Headers();
    headers.append(
      'litium-request-context',
      `{'ChannelSystemId':'${window.__litium.requestContext.channelSystemId}'}`
    );

    sunPanelService
      .getProductVariants(headers)
      .then((data) => {
        setVariantsData({ variants: data, status: REQ_STATUS.SUCCESS });
        setSelectedVariantId(data[0].id);
      })
      .catch(() => {
        setVariantsData({ variants: [], status: REQ_STATUS.ERROR });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [norkartData]);

  // ? 4 - FETCH OFFERS DATA
  useEffect(() => {
    // ? Don't fetch if we don't have parameters, since we won't get any offers
    if (!parameters || !parameters.length) return;

    setOffersData({ offers: [], status: REQ_STATUS.LOADING });

    const headers = new Headers();
    headers.append('content-type', 'application/json');

    const promises = [
      ...parameters.map((parameter) =>
        sunPanelService.getOfferData(parameter, headers)
      ),
    ];

    Promise.allSettled(promises)
      .then(async (results) => {
        const jsonData = results
          .map((result, idx) => {
            try {
              const data = JSON.parse(result.value || 'null');
              if (!data) return null;
              return {
                ...data,
                variant: parameters[idx].produkttype,
              };
            } catch (error) {
              console.error(error);
              return null;
            }
          })
          .filter(Boolean);

        setOffersData({ offers: jsonData, status: REQ_STATUS.SUCCESS });
      })
      .catch(() => {
        setOffersData({ offers: [], status: REQ_STATUS.ERROR });
      });
  }, [parameters]);

  const stepClass = (id) => {
    const classes = [];
    if (step == id) {
      classes.push('active');
    }
    if (step < id) {
      classes.push('disabled');
    }
    return classes.join(' ');
  };

  //handle offer request with correct data
  const handleOfferRequest = (formFields) => {
    if (!acceptedConditions) {
      return null;
    }

    setRequestLoading(true);
    setRequestError(false);
    setRequestSuccess(false);

    const sunPanelRequestHeader = new Headers();
    sunPanelRequestHeader.append(
      'litium-request-context',
      `{'ChannelSystemId':'${window.__litium.requestContext.channelSystemId}'}`
    );

    executeRecaptcha('sunPanelSendRequest').then((token) => {
      const sunPanelRequestParameters = JSON.stringify({
        ...formFields,
        panelCount: amountPanels,
        panelTypeId: selectedVariantId.toString(),
        roofTypeId: roofTypeId.toString(),
        takvinkel: parameters[0].helning,
        token,
      });

      const formData = new FormData();
      formData.append('formData', sunPanelRequestParameters);
      formData.append(
        'offerData',
        JSON.stringify({
          ...selectedOffer,
          paneler: [selectedPanelCombo],
        })
      );

      sunPanelService
        .sendFormRequest(formData, sunPanelRequestHeader)
        .then((response) => {
          if (response.status === 200) {
            setAcceptedConditions(false);
            setRequestSuccess(true);
            console.info('Success:', response);
          } else {
            setRequestError(true);
          }
        })
        .catch((error) => {
          console.error('Error:', error);
          setRequestError(true);
        })
        .finally(() => {
          setRequestLoading(false);
        });
    });
  };

  const restartForm = () => {
    setRequestError(false);
    setRequestSuccess(false);
    setRequestLoading(false);
  };

  const showForm = !requestLoading && !requestSuccess && !requestError;
  const shouldUserRetry = requestError && !requestLoading;

  const noOffer = selectedOffer && !selectedOffer.paneler?.length;

  if (requestSuccess) {
    return (
      <div className="sunpanels-by-roof-error">
        <div className="sunpanels-by-roof-error__title">{thanksTitle}</div>
        <div className="sunpanels-by-roof-error__text">{thanksText}</div>
        <a className="sunpanels-by-roof-error__link" href={sunPanelPageUrl}>
          {translate('solcellepanel.general.newsearch')}
        </a>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="sunpanels-by-roof-error">
        <div className="sunpanels-by-roof-error__title">
          {translate('general.loading')}
        </div>
      </div>
    );
  }

  if (!loading && !selectedOffer) {
    return (
      <div className="sunpanels-by-roof-error">
        <div className="sunpanels-by-roof-error__title">{errorTitle}</div>
        <div className="sunpanels-by-roof-error__text">{errorText}</div>
        <a className="sunpanels-by-roof-error__link" href={sunPanelPageUrl}>
          {translate('solcellepanel.general.tryagain')}
        </a>
      </div>
    );
  }

  return (
    <div className="sunpanels-by-roof">
      <div className={`checkout__step cart-step ${stepClass(STEP_RESULT)}`}>
        <div className="checkout__step-bar">
          <span className="checkout__step-title">{title}</span>
          <span
            className="checkout__step-edit"
            onClick={() => {
              setStep(STEP_RESULT);
              window.scrollTo(0, 0);
            }}
          >
            {translate('checkout.step.edit')}
          </span>
        </div>

        <div className="checkout__step-content-full-width">
          {selectedOffer && (
            <MapAndAddress
              title={mapAddressTitle}
              address={selectedOffer.adresse}
              link={sunPanelPageUrl}
              apiKey={apiKey}
            />
          )}

          <VariantSelector
            handleVariantSelect={(e) => setSelectedVariantId(e)}
            title={variantSelectorTitle}
            panelVariants={variantsData ? variantsData.variants : null}
            selectedVariantId={selectedVariantId}
          />

          {selectedOffer && !!selectedOffer.paneler?.length ? (
            <AmountSlider
              handleAmountPanels={(e) => setAmountPanels(e)}
              title={amountSliderTitle}
              value={amountPanels}
              min={MIN_PANELS}
              max={
                selectedOffer
                  ? selectedOffer.paneler[selectedOffer.paneler.length - 1]
                      .antallPaneler
                  : MIN_PANELS
              }
            />
          ) : null}

          <SellingPoints
            yearlyProduction={selectedPanelCombo ? selectedPanelCombo.kwtAar : 0}
          />

          <div className="sunpanels-by-roof__bottom-cards">
            <YourRoof
              title={yourRoofTitle}
              preamble={yourRoofPreamble}
              defaultTypePreamble={yourRoofDefaultTypePreamble}
              text={yourRoofText}
              titleModal={yourRoofModelTitle}
              handleSetRoofType={(e) => debouncedSetRoofType(e)}
              roofs={roofTypesData ? roofTypesData.roofTypes : null}
              selectedRoofType={selectedRoofType}
              isDefaultRoofType={selectedRoofType?.id === DEFAULT_ROOF_ID}
            />

            <EstimatedOfferCard
              offerIsAvailable={!noOffer}
              title={offerTitle}
              noOfferTitle={noOfferTitle}
              noOfferText={noOfferText}
              price={
                selectedPanelCombo
                  ? calculateComboPrice(selectedPanelCombo)
                  : null
              }
              supportDiscount={supportDiscount}
            />
          </div>
          <div className="checkout__step-content-next-button">
            <button
              className="button"
              type="button"
              onClick={() => {
                setStep(STEP_MAKE_CONTACT);
                window.scrollTo(0, 0);
              }}
            >
              {translate('solcellepanel.estimated.offer.nestStage')}
            </button>
          </div>
        </div>
      </div>
      <div className={`checkout__step cart-step ${stepClass(STEP_MAKE_CONTACT)}`}>
        <div className="checkout__step-bar">
          <span className="checkout__step-title">
            {translate('solcellepanel.general.contactinfotitle')}
          </span>
          <span
            className="checkout__step-edit"
            onClick={() => {
              setStep(STEP_MAKE_CONTACT);
              window.scrollTo(0, 0);
            }}
          >
            {translate('checkout.step.edit')}
          </span>
        </div>

        <div className="checkout__step-content">
          {requestLoading ? (
            <>
              <div className="sunpanels-by-roof-error">
                <div className="sunpanels-by-roof-error__title">
                  {translate('general.loading')}
                </div>
              </div>
            </>
          ) : null}

          {shouldUserRetry ? (
            <>
              <div className="flex-container flex-dir-column align-center margin-3">
                <div className="sunpanels-by-roof-error__title">
                  {translate('solcellepanel.general.formErrorTitle')}
                </div>
                <button className="button margin-top-2" onClick={restartForm}>
                  {translate('solcellepanel.general.tryagain')}
                </button>
              </div>
            </>
          ) : null}

          {showForm ? (
            <>
              <div className="sunpanel-offer-request-form__title">
                {translate('solcellepanel.general.yourinfotitle')}
              </div>
              <section id="sunpanel-offer-request">
                <SunPanelsContactInfoUserForm
                  acceptedConditions={acceptedConditions}
                  handleOfferRequest={handleOfferRequest}
                />
              </section>
              <div className="sunpanel-offer-request-form-checkbox__container">
                <input
                  className="sunpanel-offer-request-form-checkbox__box"
                  name="Accept"
                  id="sunpanel-offer-customer-accept"
                  required
                  type="checkbox"
                  checked={acceptedConditions}
                  onChange={() => setAcceptedConditions((prev) => !prev)}
                />
                <label className="sunpanel-offer-request-form-checkbox__text">
                  {translate('solcellepanel.general.readandacceptconditionstext')}
                </label>
              </div>
              <div className="sunpanels-by-roof-condition">
                <a
                  href=""
                  className={!hiddenTermsAndConditions ? 'chevron-up' : ''}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setHiddenTermsAndConditions(!hiddenTermsAndConditions);
                  }}
                >
                  {translate('solcellepanel.general.seecondition')}
                </a>
                {!hiddenTermsAndConditions && (
                  <div>
                    {translate('solcellepanel.terms-and-condition-description')}
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default withGoogleReCaptcha(SunPanelsByRoof);

const Props = {
  title: PropTypes.string,
  mapAddressTitle: PropTypes.string,
  sunPanelPageUrl: PropTypes.string,
  variantSelectorTitle: PropTypes.string,
  amountSliderTitle: PropTypes.string,
  yourRoofTitle: PropTypes.string,
  yourRoofPreamble: PropTypes.string,
  yourRoofDefaultTypePreamble: PropTypes.string,
  yourRoofText: PropTypes.string,
  offerTitle: PropTypes.string,
  errorTitle: PropTypes.string,
  errorText: PropTypes.string,
  noOfferTitle: PropTypes.string,
  noOfferText: PropTypes.string,
  thanksTitle: PropTypes.string,
  thanksText: PropTypes.string,
  yourRoofModelTitle: PropTypes.string,
  apiKey: PropTypes.string,
  googleReCaptchaProps: PropTypes.object,
};

SunPanelsByRoof.propTypes = Props;
